// Client mirror of supabase/functions/_shared/productMatch.ts
// Keep in sync with that file — copy of normalizeProductName + normalizeCore.
// Server file is the source of truth for the full matching engine.
//
// Desde 2026-08 también viaja aquí el EMPAREJADOR ENTERO (matchProduct y el tier
// «casi idéntico»): el escáner lo necesita para volver a emparejar cuando alguien
// corrige el nombre a mano, el alta manual para avisar de «ya tienes este», y la
// auditoría de duplicados para agrupar DINAMIT / DYNAMITE / DINAMITE. Es una copia
// literal del servidor; src/__tests__/productMatch.fuzzy.test.ts vigila que las dos
// no se separen.

const UNIT_WORDS = ["KILOS", "LITROS", "GRAMOS", "GRS", "LTS", "KG", "GR", "ML", "CC", "UDS", "LT", "UD", "L", "G"];
const STOPWORDS = new Set(["ECO", "BIO", "SG", "MS", "NO", "RETORNABLE", "DE", "EL", "LA", "LOS", "LAS", "DEL", "Y", "CON", "PARA"]);
const FORMULATION_CODES = ["OD", "SC", "SG", "WG", "WP", "EC", "EW", "CS", "SL", "ME", "ZC", "DG", "GR"];

function foldAccents(s: string): string {
  return s
    .replace(/[ÁÀÄÂÃ]/g, "A")
    .replace(/[ÉÈËÊ]/g, "E")
    .replace(/[ÍÌÏÎ]/g, "I")
    .replace(/[ÓÒÖÔÕ]/g, "O")
    .replace(/[ÚÙÜÛ]/g, "U")
    .replace(/[Ñ]/g, "N")
    .replace(/[Ç]/g, "C");
}

export function normalizeProductName(input: string | null | undefined): string {
  if (!input) return "";
  let s = String(input);
  s = s.toUpperCase();
  s = foldAccents(s);
  s = s.replace(/,/g, ".").replace(/×/g, "X").replace(/[*º°ª"'`!]/g, "");
  s = s.replace(/\(([^)]*\d[^)]*)\)/g, (m) => {
    const inner = m.slice(1, -1).trim();
    if (/\d\s*X\s*\d/.test(inner)) return " ";
    const sizeRe = new RegExp(`^\\d+(\\.\\d+)?\\s*(${UNIT_WORDS.join("|")})$`, "i");
    if (sizeRe.test(inner.replace(/\s+/g, " "))) return " ";
    return " ";
  });
  s = s.replace(/[()\[\]{}]/g, " ");
  s = s.replace(/[\-_/;:,+·]/g, " ");
  s = s.replace(/(?<!\d)\.(?!\d)|\.(?=\D)|(?<=\D)\./g, " ");
  const unitRe = new RegExp(`\\b\\d+(\\.\\d+)?\\s*(${UNIT_WORDS.join("|")})\\b`, "gi");
  s = s.replace(unitRe, " ");
  const packRe = /\b\d+\s*X\s*\d+(\.\d+)?\b/gi;
  s = s.replace(packRe, " ");
  s = s.replace(/\s+/g, " ").trim();
  return s;
}

export function normalizeCore(input: string | null | undefined): string {
  const base = normalizeProductName(input);
  if (!base) return "";
  const tokens = base
    .split(" ")
    .filter((t) => t.length > 0 && !STOPWORDS.has(t));
  return tokens.join(" ").replace(/\s+/g, " ").trim();
}

export function normalizeRegistration(reg: string | null | undefined): string | null {
  if (!reg) return null;
  const s = String(reg).toUpperCase().replace(/[^A-Z0-9]/g, "");
  /* UN SOLO ESPACIO DE NOMBRES ES (C1d, revisado el 26-09-2026). Todo nº con
     «ES» delante se queda con su «ES»: ES-00554 → «ES554», ES-00054 → «ES54»,
     ES-01405 → «ES1405». Los nacionales del MAPA son solo cifras y empiezan en
     el 11179, así que un nº sin «ES» nunca es un ES: el «554» de COSTAR (que es
     el 22060) NO es el ES-00554 de DAGONIS, ni un «54» suelto el ES-00054 de
     ULTRA-PROM. Hasta hoy el «ES» se quitaba y los dos espacios se mezclaban: la
     parada de duplicados habría dicho que DAGONIS «ya existe» como COSTAR, y los
     60 ES-00001…ES-00099 se tiraban por cortos. Detrás del «ES» solo valen
     cifras (el MAPA no publica otra cosa): «ES-A1», «Es - xxxxxx» o «ES-00» no
     son un nº.
     Es LA regla de todo el nº de registro: la clave del resolvedor de los PDF
     (`registryKey`, que llama a esta), `registro_norm` de la copia del MAPA (el
     sync) y SQL (`normalize_registration_number`, la parada DUPLICATE_PRODUCT).
     Si cambia aquí, cambia en las tres; productMatch.registration.test lo vigila. */
  const conPrefijoES = s.startsWith("ES");
  const resto = (conPrefijoES ? s.slice(2) : s).replace(/^0+/, "");
  if (conPrefijoES) return /^[0-9]+$/.test(resto) ? `ES${resto}` : null;
  if (resto.length < 3) return null;
  /* TIENE QUE LLEVAR ALGÚN DÍGITO, o no es un número de registro.
     Los de verdad son numéricos (25353, ES-00123): de los 143 productos con
     registro guardado, 131 son solo dígitos. Los otros 12 llevan «EEEE»,
     «EEEEE», «EEEEEE», «XXXXX» o «XXXXXX» — lo que alguien tecleó para pasar
     de un campo obligatorio. Como el único filtro era «tres caracteres o más»,
     esas cadenas se tomaban por registros válidos, y la auditoría de duplicados
     agrupaba por ellas: seis productos SIN NADA QUE VER (Altacor, Kanemite,
     Lalguard M-52, Switch one, Teppeki, Verimark) salían como el mismo, con
     confianza «alta» y su botón de fusionar al lado. Fusionarlos se habría
     llevado por delante cinco productos y su stock. Se mira DESPUÉS de quitar
     los ceros: «0ABC» tampoco es un nº. */
  if (!/[0-9]/.test(resto)) return null;
  return resto;
}

export function unitFamily(u?: string | null): "mass" | "volume" | "count" | null {
  if (!u) return null;
  const k = String(u).trim();
  if (["kg", "KG", "Kg", "g", "G"].includes(k)) return "mass";
  if (["L", "l", "ml", "mL", "ML"].includes(k)) return "volume";
  if (["units", "ud", "uds", "UD", "UDS"].includes(k)) return "count";
  return null;
}

// ===========================================================================
// Emparejador (copia del servidor a partir de aquí)
// ===========================================================================

export interface Signatures {
  percents: string[];
  formulations: string[];
  formCodes: string[];
  bareNumbers: string[];
}

export function extractSignatures(name: string | null | undefined): Signatures {
  const out: Signatures = { percents: [], formulations: [], formCodes: [], bareNumbers: [] };
  if (!name) return out;
  const raw = foldAccents(String(name).toUpperCase()).replace(/,/g, ".").replace(/×/g, "X");
  const percentRe = /(\d+(?:\.\d+)?)\s*%/g;
  let m: RegExpExecArray | null;
  while ((m = percentRe.exec(raw)) !== null) {
    out.percents.push(m[1]);
  }
  const formRe = new RegExp(`(\\d+(?:\\.\\d+)?)\\s*(${FORMULATION_CODES.join("|")})\\b`, "gi");
  while ((m = formRe.exec(raw)) !== null) {
    out.formulations.push(`${m[1]} ${m[2].toUpperCase()}`);
    out.formCodes.push(m[2].toUpperCase());
  }
  const BARE_SAFE = ["EC", "SC", "WG", "WP", "EW", "CS", "OD", "ZC", "DG", "ME", "SG"];
  for (const tok of raw.split(/[^A-Z0-9.]+/)) {
    const up = tok.toUpperCase();
    if (BARE_SAFE.includes(up) && !out.formCodes.includes(up)) out.formCodes.push(up);
  }
  const packagingNums = new Set<string>();
  const packRe = /\b(\d+)\s*X\s*(\d+(?:\.\d+)?)\b/gi;
  while ((m = packRe.exec(raw)) !== null) {
    packagingNums.add(m[1]);
    packagingNums.add(m[2]);
  }
  const unitRe = new RegExp(`\\b(\\d+(?:\\.\\d+)?)\\s*(${UNIT_WORDS.join("|")})\\b`, "gi");
  while ((m = unitRe.exec(raw)) !== null) {
    packagingNums.add(m[1]);
  }
  const bareRe = /\b(\d+(?:\.\d+)?)\b/g;
  while ((m = bareRe.exec(raw)) !== null) {
    const num = m[1];
    if (packagingNums.has(num)) continue;
    const rest = raw.slice(m.index + m[0].length);
    if (/^\s*%/.test(rest)) continue;
    const followRe = new RegExp(`^\\s*(${FORMULATION_CODES.join("|")})\\b`, "i");
    if (followRe.test(rest)) continue;
    out.bareNumbers.push(num);
  }
  return out;
}

export type Compatibility = "compatible" | "soft_mismatch" | "hard_mismatch" | "incompatible";
export interface SignatureCheck { compatibility: Compatibility; reasons: string[]; }

export function compareSignatures(a: Signatures, b: Signatures): SignatureCheck {
  const reasons: string[] = [];
  const setEq = (x: string[], y: string[]) =>
    x.length === y.length && x.every((v) => y.includes(v));

  if (setEq(a.percents, b.percents) && setEq(a.formulations, b.formulations) && setEq(a.bareNumbers, b.bareNumbers)) {
    return { compatibility: "compatible", reasons };
  }
  const aBare = new Set(a.bareNumbers);
  const bBare = new Set(b.bareNumbers);
  if (aBare.size > 0 && bBare.size > 0 && ![...aBare].every((x) => bBare.has(x))) {
    reasons.push("La concentración no coincide");
    return { compatibility: "hard_mismatch", reasons };
  }
  const aPct = new Set(a.percents);
  const bPct = new Set(b.percents);
  if (aPct.size > 0 && bPct.size > 0 && ![...aPct].every((x) => bPct.has(x))) {
    reasons.push("La concentración no coincide");
    return { compatibility: "hard_mismatch", reasons };
  }
  const aFormNums = a.formulations.map((f) => f.split(" ")[0]);
  const bFormNums = b.formulations.map((f) => f.split(" ")[0]);
  if (aFormNums.length > 0 && b.bareNumbers.length > 0) {
    const eq = aFormNums.length === b.bareNumbers.length && aFormNums.every((n) => b.bareNumbers.includes(n));
    if (!eq) {
      reasons.push("La concentración no coincide");
      return { compatibility: "soft_mismatch", reasons };
    }
  }
  if (bFormNums.length > 0 && a.bareNumbers.length > 0) {
    const eq = bFormNums.length === a.bareNumbers.length && bFormNums.every((n) => a.bareNumbers.includes(n));
    if (!eq) {
      reasons.push("La concentración no coincide");
      return { compatibility: "soft_mismatch", reasons };
    }
  }
  if ((aPct.size > 0) !== (bPct.size > 0)) {
    reasons.push("Concentración solo en un producto");
    return { compatibility: "soft_mismatch", reasons };
  }
  if (a.formCodes.length > 0 && b.formCodes.length > 0) {
    const aC = new Set(a.formCodes), bC = new Set(b.formCodes);
    const shared = [...aC].some((c) => bC.has(c));
    if (!shared) {
      reasons.push(`Formulación distinta (${[...aC].join("/")} vs ${[...bC].join("/")})`);
      return { compatibility: "incompatible", reasons };
    }
  }
  if ((a.formulations.length > 0) !== (b.formulations.length > 0)) {
    reasons.push("Sufijo de formulación omitido");
    return { compatibility: "compatible", reasons };
  }
  if (a.formulations.length > 0 && b.formulations.length > 0 && !setEq(a.formulations, b.formulations)) {
    reasons.push("Sufijo de formulación distinto");
    return { compatibility: "soft_mismatch", reasons };
  }
  if ((a.bareNumbers.length > 0) !== (b.bareNumbers.length > 0)) {
    reasons.push("Concentración solo en un producto");
    return { compatibility: "soft_mismatch", reasons };
  }
  return { compatibility: "compatible", reasons };
}

/** Distancia Damerau-Levenshtein (OSA): inserción, borrado, sustitución y transposición. */
export function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  const la = a.length, lb = b.length;
  if (la === 0) return lb;
  if (lb === 0) return la;
  const d: number[][] = Array.from({ length: la + 1 }, () => new Array<number>(lb + 1).fill(0));
  for (let i = 0; i <= la; i++) d[i][0] = i;
  for (let j = 0; j <= lb; j++) d[0][j] = j;
  for (let i = 1; i <= la; i++) {
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[la][lb];
}

/** Cuántos errores se toleran en una palabra según su longitud: 1 hasta 7 letras, 2 desde 8. */
function allowedEdits(len: number): number {
  if (len < 4) return 0;
  return len >= 8 ? 2 : 1;
}

export interface FuzzyNameResult {
  /** true = casi el mismo nombre: marca a ≤1-2 letras y el resto del nombre casi igual. */
  similar: boolean;
  /** 0..1 sobre el nombre normalizado sin espacios (1 = idéntico). */
  score: number;
  /** Marca (primer token del núcleo) a distancia tolerable. */
  brandClose: boolean;
}

/**
 * ¿Son «casi» el mismo nombre? Dos condiciones, las dos obligatorias:
 *  1) la MARCA (primer token del núcleo) coincide o está a 1-2 letras
 *     (DINAMIT/DYNAMITE/DINAMITE; AFIRM/AFFIRM; OROQUIN/ORO QUIN);
 *  2) el nombre entero sin espacios se parece ≥ 0,8 (así «ORTIVA» no se
 *     confunde con «ORTIVA TOP», ni «KARATE» con «KARATE ZEON»).
 * Concentraciones y formulaciones NO se miran aquí: eso lo decide
 * compareSignatures, como en el resto de tiers.
 */
export function fuzzyNameMatch(a: string | null | undefined, b: string | null | undefined): FuzzyNameResult {
  const pa = fuzzyParts(a);
  const pb = fuzzyParts(b);
  if (!pa || !pb || !pa.brand || !pb.brand) return { similar: false, score: 0, brandClose: false };
  let wordsA = pa.words;
  let wordsB = pb.words;
  let brandClose = pa.brand === pb.brand;
  if (!brandClose) {
    const dist = editDistance(pa.brand, pb.brand);
    brandClose = dist <= allowedEdits(Math.min(pa.brand.length, pb.brand.length));
  }
  if (!brandClose) {
    // «ORO QUIN» ↔ «OROQUIN»: la marca partida en dos tokens.
    if (wordsA.length > 0 && pa.brand + wordsA[0] === pb.brand) { brandClose = true; wordsA = wordsA.slice(1); }
    else if (wordsB.length > 0 && pb.brand + wordsB[0] === pa.brand) { brandClose = true; wordsB = wordsB.slice(1); }
  }
  const sameSet = (x: string[], y: string[]) => x.length === y.length && x.every((v) => y.includes(v));
  const joinedA = pa.tokens.join("");
  const joinedB = pb.tokens.join("");
  const maxLen = Math.max(joinedA.length, joinedB.length);
  const score = maxLen === 0 ? 0 : 1 - editDistance(joinedA, joinedB) / maxLen;
  // Misma marca (a 1-2 letras), mismas palabras restantes y mismos números: «AZUFRE
  // 80» y «AZUFRE 50» no son el mismo, «ORTIVA» y «ORTIVA TOP» tampoco.
  const similar = brandClose && sameSet(pa.numbers, pb.numbers) && sameSet(wordsA, wordsB);
  return { similar, score, brandClose };
}

/* Tokens de envase que el normalizador general no quita (ese está atado al SQL y no
   se toca): «5 KGS», «LITRO»… Solo para el tier fuzzy. */
const FUZZY_UNIT_LIKE = new Set([...UNIT_WORDS, "KGS", "LITRO", "LITROS", "KILO", "KILOS", "UNIDAD", "UNIDADES", "GRAMO", "GRAMOS", "MILILITROS"]);

/** Marca, palabras restantes y números de un nombre, sin tokens de envase. */
function fuzzyParts(name: string | null | undefined): { brand: string; words: string[]; numbers: string[]; tokens: string[] } | null {
  const core = normalizeCore(name);
  if (!core) return null;
  const toks = core.split(" ").filter(Boolean);
  const kept: string[] = [];
  for (let i = 0; i < toks.length; i++) {
    const t = toks[i];
    if (FUZZY_UNIT_LIKE.has(t)) continue;
    if (/^\d+(\.\d+)?$/.test(t) && i + 1 < toks.length && FUZZY_UNIT_LIKE.has(toks[i + 1])) { i++; continue; }
    kept.push(t);
  }
  const alpha = kept.filter((t) => !/^\d/.test(t));
  const numbers = kept.filter((t) => /^\d/.test(t));
  const brand = alpha[0] ?? "";
  return { brand, words: alpha.slice(1), numbers, tokens: kept };
}

function tokenWeight(tok: string, idx: number): number {
  if (idx === 0) return 3;
  if (tok.length >= 4) return 1;
  if (tok.length >= 2) return 0.5;
  return 0.25;
}

function weightedJaccardScore(aName: string, bName: string): number {
  const aTok = normalizeCore(aName).split(" ").filter(Boolean);
  const bTok = normalizeCore(bName).split(" ").filter(Boolean);
  if (aTok.length === 0 || bTok.length === 0) return 0;
  const aWeights = new Map<string, number>();
  aTok.forEach((t, i) => aWeights.set(t, Math.max(aWeights.get(t) ?? 0, tokenWeight(t, i))));
  const bWeights = new Map<string, number>();
  bTok.forEach((t, i) => bWeights.set(t, Math.max(bWeights.get(t) ?? 0, tokenWeight(t, i))));
  const all = new Set([...aWeights.keys(), ...bWeights.keys()]);
  let interW = 0, unionW = 0;
  for (const tok of all) {
    const wa = aWeights.get(tok) ?? 0;
    const wb = bWeights.get(tok) ?? 0;
    interW += Math.min(wa, wb);
    unionW += Math.max(wa, wb);
  }
  return unionW === 0 ? 0 : interW / unionW;
}

export interface ScannedProductLike {
  name: string;
  registration?: string | null;
  unit?: string | null;
  supplier?: string | null;
}
export interface ExistingProductLike {
  id: string;
  product_name: string;
  registration_number?: string | null;
  unit?: string | null;
  supplier_name?: string | null;
}
export interface MatchCandidate {
  id: string;
  name: string;
  score: number;
}
export interface MatchResult {
  action: "add_to_existing" | "confirm_match" | "create_new";
  existing_id: string | null;
  existing_name: string | null;
  existing_unit: string | null;
  existing_supplier: string | null;
  confidence: number;
  reasons: string[];
  supplier_differs: boolean;
  unit_conversion_needed: boolean;
  candidates?: MatchCandidate[];
}

function applyUnitFamilyDowngrade(
  scanned: ScannedProductLike,
  existing: ExistingProductLike,
  baseReasons: string[],
): { action: "add_to_existing" | "confirm_match"; reasons: string[] } {
  const fs = unitFamily(scanned.unit);
  const fe = unitFamily(existing.unit);
  if (fs && fe && fs !== fe) {
    return { action: "confirm_match", reasons: [...baseReasons, "Unidades de familia distinta — confirmar"] };
  }
  return { action: "add_to_existing", reasons: baseReasons };
}

function makeResult(
  action: MatchResult["action"],
  existing: ExistingProductLike | null,
  scanned: ScannedProductLike,
  confidence: number,
  reasons: string[],
  candidates?: MatchCandidate[],
): MatchResult {
  const supDiff =
    !!(scanned.supplier && existing?.supplier_name &&
      normalizeCore(scanned.supplier) !== "" &&
      normalizeCore(existing.supplier_name) !== "" &&
      normalizeCore(scanned.supplier) !== normalizeCore(existing.supplier_name));
  const fa = unitFamily(scanned.unit);
  const fb = unitFamily(existing?.unit);
  const conv = !!(scanned.unit && existing?.unit && scanned.unit !== existing.unit && fa && fb && fa === fb);
  return {
    action,
    existing_id: existing?.id ?? null,
    existing_name: existing?.product_name ?? null,
    existing_unit: existing?.unit ?? null,
    existing_supplier: existing?.supplier_name ?? null,
    confidence: Math.max(0, Math.min(1, confidence)),
    reasons,
    supplier_differs: supDiff,
    unit_conversion_needed: conv,
    candidates,
  };
}

/** Misma lógica (y mismos umbrales) que el servidor. */
export function matchProduct(scanned: ScannedProductLike, existing: ExistingProductLike[]): MatchResult {
  if (!scanned?.name || !Array.isArray(existing)) {
    return makeResult("create_new", null, scanned || { name: "" }, 0, ["Producto nuevo"]);
  }

  // T1: registration numbers equal
  const scannedReg = normalizeRegistration(scanned.registration);
  if (scannedReg) {
    for (const ep of existing) {
      const er = normalizeRegistration(ep.registration_number);
      if (er && er === scannedReg) {
        const nameSim = weightedJaccardScore(scanned.name, ep.product_name);
        if (nameSim < 0.3) {
          return makeResult("confirm_match", ep, scanned, 0.9, ["Nº de registro coincide pero el nombre difiere — confirmar"]);
        }
        const t1 = applyUnitFamilyDowngrade(scanned, ep, ["Nº de registro coincide"]);
        return makeResult(t1.action, ep, scanned, 0.99, t1.reasons);
      }
    }
  }

  // T2: normalized product name equal
  const scannedNorm = normalizeProductName(scanned.name);
  if (scannedNorm) {
    for (const ep of existing) {
      if (normalizeProductName(ep.product_name) === scannedNorm) {
        const t2 = applyUnitFamilyDowngrade(scanned, ep, ["Nombre normalizado coincide"]);
        return makeResult(t2.action, ep, scanned, 0.97, t2.reasons);
      }
    }
  }

  const scannedSigs = extractSignatures(scanned.name);

  // T2b: nombre casi idéntico → siempre confirm_match (ver fuzzyNameMatch).
  let bestFuzzy: { ep: ExistingProductLike; score: number; sig: SignatureCheck } | null = null;
  for (const ep of existing) {
    const fz = fuzzyNameMatch(scanned.name, ep.product_name);
    if (!fz.similar) continue;
    const sig = compareSignatures(scannedSigs, extractSignatures(ep.product_name));
    if (sig.compatibility === "hard_mismatch" || sig.compatibility === "incompatible") continue;
    if (!bestFuzzy || fz.score > bestFuzzy.score) bestFuzzy = { ep, score: fz.score, sig };
  }
  if (bestFuzzy) {
    const reasons = ["Nombre casi idéntico — ¿error de lectura?", ...bestFuzzy.sig.reasons];
    const fam = applyUnitFamilyDowngrade(scanned, bestFuzzy.ep, reasons);
    return makeResult("confirm_match", bestFuzzy.ep, scanned, Math.min(0.89, bestFuzzy.score), fam.reasons, [
      { id: bestFuzzy.ep.id, name: bestFuzzy.ep.product_name, score: +bestFuzzy.score.toFixed(3) },
    ]);
  }

  type Scored = { ep: ExistingProductLike; score: number; sig: SignatureCheck };
  const scored: Scored[] = existing
    .map((ep) => {
      const score = weightedJaccardScore(scanned.name, ep.product_name);
      const sig = compareSignatures(scannedSigs, extractSignatures(ep.product_name));
      return { ep, score, sig };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score);

  const topCandidates: MatchCandidate[] = scored.slice(0, 3).map((s) => ({
    id: s.ep.id,
    name: s.ep.product_name,
    score: +s.score.toFixed(3),
  }));

  const best = scored[0];
  if (!best) {
    return makeResult("create_new", null, scanned, 0, ["Producto nuevo"]);
  }
  if (best.sig.compatibility === "incompatible") {
    return makeResult("create_new", null, scanned, 0, best.sig.reasons, topCandidates);
  }
  if (best.score >= 0.85 && best.sig.compatibility === "compatible") {
    const t3 = applyUnitFamilyDowngrade(scanned, best.ep, ["Coincidencia por tokens", ...best.sig.reasons]);
    return makeResult(t3.action, best.ep, scanned, best.score, t3.reasons, t3.action === "confirm_match" ? topCandidates : undefined);
  }
  if (best.score >= 0.85 && best.sig.compatibility === "soft_mismatch") {
    return makeResult("confirm_match", best.ep, scanned, best.score * 0.8, ["Requiere confirmación", ...best.sig.reasons], topCandidates);
  }
  if (best.sig.compatibility === "hard_mismatch") {
    if (best.score >= 0.6) {
      return makeResult("confirm_match", best.ep, scanned, best.score * 0.8, ["Requiere confirmación", ...best.sig.reasons], topCandidates);
    }
  }
  if (best.score >= 0.6 && (best.sig.compatibility === "compatible" || best.sig.compatibility === "soft_mismatch")) {
    return makeResult("confirm_match", best.ep, scanned, best.score * 0.8, ["Requiere confirmación", ...best.sig.reasons], topCandidates);
  }
  return makeResult("create_new", null, scanned, best.score, ["Producto nuevo"], topCandidates);
}

/**
 * Para el ALTA MANUAL y avisos en línea: ¿a qué producto ya guardado se parece este
 * nombre? Devuelve el mejor candidato (idéntico normalizado, mismo registro o casi
 * idéntico) o null. Es matchProduct con el resultado reducido a «¿hay alguien?».
 */
export function findLikelyExisting(
  name: string,
  registration: string | null | undefined,
  unit: string | null | undefined,
  existing: ExistingProductLike[],
): { id: string; name: string; reason: string; exact: boolean } | null {
  const r = matchProduct({ name, registration: registration ?? null, unit: unit ?? null }, existing);
  if (r.action === "create_new" || !r.existing_id || !r.existing_name) return null;
  return {
    id: r.existing_id,
    name: r.existing_name,
    reason: r.reasons[0] ?? "",
    exact: r.action === "add_to_existing",
  };
}
